/**
 * Structured data helpers for SEO
 */

interface BreadcrumbItem {
  name: string
  url: string
}

interface PropertyData {
  name: string
  description?: string
  image?: string | string[]
  address?: {
    streetAddress?: string
    addressLocality?: string
    addressRegion?: string
    postalCode?: string
    addressCountry?: string
  }
  priceRange?: string
  telephone?: string
  url?: string
  checkinTime?: string
  checkoutTime?: string
  amenityFeature?: Array<{ name: string; value?: string }>
  starRating?: {
    ratingValue: number
    bestRating?: number
  }
}

interface RoomData {
  name: string
  description?: string
  image?: string | string[]
  offers?: {
    price: number
    priceCurrency?: string
    availability?: 'InStock' | 'OutOfStock' | 'PreOrder'
    validFrom?: string
    validThrough?: string
  }
  bed?: {
    type: string
    quantity?: number
  }
  occupancy?: {
    minValue?: number
    maxValue?: number
  }
  amenityFeature?: Array<{ name: string }>
}

interface EventData {
  name: string
  description?: string
  startDate: string
  endDate?: string
  location?: {
    name: string
    address?: string
  }
  image?: string | string[]
  offers?: {
    price?: number
    priceCurrency?: string
    availability?: string
    url?: string
  }
  performer?: Array<{ name: string; type?: string }>
  organizer?: {
    name: string
    url?: string
  }
}

/**
 * Generate Organization structured data
 */
export const createOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '北海道ボールパークFビレッジ',
  alternateName: 'HOKKAIDO BALLPARK F VILLAGE',
  url: 'https://www.hkdballpark.com/',
  logo: 'https://booking.hkdballpark.com/img/Header/header-logo.png',
  sameAs: [
    'https://www.facebook.com/hokkaido.ballpark.fvillage',
    'https://twitter.com/ballpark_htb',
    'https://www.instagram.com/hokkaido_ballpark_fvillage/',
  ],
})

/**
 * Generate WebSite structured data with search action
 */
export const createWebSiteSchema = (siteUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '北海道ボールパークFビレッジ 宿泊予約',
  alternateName: 'F Village Hotel Booking',
  url: siteUrl,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${siteUrl}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
})

/**
 * Generate BreadcrumbList structured data
 */
export const createBreadcrumbSchema = (items: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
})

/**
 * Generate Hotel/LodgingBusiness structured data
 */
export const createHotelSchema = (data: PropertyData) => ({
  '@context': 'https://schema.org',
  '@type': 'Hotel',
  name: data.name,
  description: data.description,
  image: data.image,
  url: data.url,
  telephone: data.telephone,
  priceRange: data.priceRange,
  address: data.address
    ? {
        '@type': 'PostalAddress',
        ...data.address,
      }
    : undefined,
  checkinTime: data.checkinTime,
  checkoutTime: data.checkoutTime,
  amenityFeature: data.amenityFeature?.map((amenity) => ({
    '@type': 'LocationFeatureSpecification',
    name: amenity.name,
    value: amenity.value,
  })),
  starRating: data.starRating
    ? {
        '@type': 'Rating',
        ratingValue: data.starRating.ratingValue,
        bestRating: data.starRating.bestRating || 5,
      }
    : undefined,
})

/**
 * Generate HotelRoom structured data
 */
export const createHotelRoomSchema = (data: RoomData, hotelName: string) => ({
  '@context': 'https://schema.org',
  '@type': 'HotelRoom',
  name: data.name,
  description: data.description,
  image: data.image,
  bed: data.bed
    ? {
        '@type': 'BedDetails',
        typeOfBed: data.bed.type,
        numberOfBeds: data.bed.quantity,
      }
    : undefined,
  occupancy: data.occupancy
    ? {
        '@type': 'QuantitativeValue',
        minValue: data.occupancy.minValue,
        maxValue: data.occupancy.maxValue,
      }
    : undefined,
  amenityFeature: data.amenityFeature?.map((amenity) => ({
    '@type': 'LocationFeatureSpecification',
    name: amenity.name,
  })),
  offers: data.offers
    ? {
        '@type': 'Offer',
        price: data.offers.price,
        priceCurrency: data.offers.priceCurrency || 'JPY',
        availability: `https://schema.org/${
          data.offers.availability || 'InStock'
        }`,
        validFrom: data.offers.validFrom,
        validThrough: data.offers.validThrough,
      }
    : undefined,
  containedInPlace: {
    '@type': 'Hotel',
    name: hotelName,
  },
})

/**
 * Generate Event structured data (for F Village events)
 */
export const createEventSchema = (data: EventData) => ({
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: data.name,
  description: data.description,
  startDate: data.startDate,
  endDate: data.endDate || data.startDate,
  image: data.image,
  location: data.location
    ? {
        '@type': 'Place',
        name: data.location.name,
        address: data.location.address,
      }
    : undefined,
  offers: data.offers
    ? {
        '@type': 'Offer',
        price: data.offers.price,
        priceCurrency: data.offers.priceCurrency || 'JPY',
        availability: data.offers.availability,
        url: data.offers.url,
      }
    : undefined,
  performer: data.performer?.map((p) => ({
    '@type': p.type || 'Person',
    name: p.name,
  })),
  organizer: data.organizer
    ? {
        '@type': 'Organization',
        name: data.organizer.name,
        url: data.organizer.url,
      }
    : undefined,
})

/**
 * Generate LocalBusiness structured data (for spa, activities)
 */
export const createLocalBusinessSchema = (
  data: PropertyData,
  type:
    | 'HealthAndBeautyBusiness'
    | 'SportsActivityLocation' = 'HealthAndBeautyBusiness'
) => ({
  '@context': 'https://schema.org',
  '@type': type,
  name: data.name,
  description: data.description,
  image: data.image,
  url: data.url,
  telephone: data.telephone,
  priceRange: data.priceRange,
  address: data.address
    ? {
        '@type': 'PostalAddress',
        ...data.address,
      }
    : undefined,
})

/**
 * Combine multiple schemas into an array
 */
export const combineSchemas = (...schemas: object[]) => schemas.filter(Boolean)
